import _debounce from 'lodash/debounce';

export default class Like {
  constructor(classname, sns_animation = false, rootElement = document) {
    this.sns_animation = sns_animation;
    this.classname = classname;
    this.$buttons = rootElement.querySelectorAll(this.classname);
    this.hide_event = null;
    this.long_touch_event = null;

    if (this.$buttons.length < 1) return;

    this.$buttons.forEach((elem) => this.clickEvent(elem));
    this.$buttons.forEach((elem) => this.hoverEvent(elem));
    this.$buttons.forEach((elem) => this.touchEvent(elem));
  }

  animationend(e) {
    setTimeout(() => {
      e.target.classList.add('like-anime-hidden');
      e.target.classList.remove('like-animation');
    }, 500);
  }

  hoverEvent(elem) {
    const reactionPopup = document.querySelector(
      `${this.classname}-popup[data-value="${elem.dataset.value}"]`
    );
    if (!reactionPopup) return;

    elem.addEventListener(
      'mouseout',
      () => {
        this.popupHide(reactionPopup);
      }
    );
    elem.addEventListener(
      'mouseover',
      () => {
        this.popupShowWithTimer(reactionPopup);
        reactionPopup.addEventListener('mouseover', () => {
          this.popupShow(reactionPopup);
        });
        reactionPopup.addEventListener('mouseout', () => {
          this.popupHide(reactionPopup);
        });
      }
    );
  }
  clickEvent(elem) {
    const eyecatch = document.querySelector(
      `${this.classname}-animation[data-value="${elem.dataset.value}"]`
    );
    const reactionPopup = document.querySelector(
      `${this.classname}-popup[data-value="${elem.dataset.value}"]`
    );

    if (this.sns_animation === true) {
      eyecatch.removeEventListener('animationend', this.animationend);
      eyecatch.addEventListener('animationend', this.animationend);
    }

    elem.addEventListener(
      'click',
      _debounce(async () => {
        // 詳細画面でリアクションポップアップ内のボタンを押下した場合、非表示対象をいいねボタンにする
        const hideButton = elem.classList.contains('c-Button-reaction')
          ? document.querySelector(
            `.c-Button-like:not(.hidden)[data-key="${elem.dataset.key}"][data-value="${elem.dataset.value}"]`
            )
          : elem;

        const allRelatedButtons = elem.parentNode.querySelectorAll('.js-like, .js-like-sns');
        allRelatedButtons.forEach(btn => btn.disabled = true);

        try {
          const displayReactionType
            = this.determineReactionType(hideButton.dataset.reactionType, elem.dataset.reactionType);
          const method = displayReactionType === 'none' ? 'delete' : 'post';

          this.updateUI(hideButton, elem, displayReactionType, reactionPopup, method, eyecatch);
          const response = await this.SendReactionRequest(elem.dataset, method);

          if (!response.ok) {
            console.error('Like request failed');
          }
        } catch (error) {
          console.error('Like request failed:', error);
        } finally {
          allRelatedButtons.forEach(btn => btn.disabled = false);
        }
      }, 400)
    );
  }

  /**
   * 表示させるボタンのリアクションタイプを決定する
   * @param {string} hideReactionType - 非表示にするボタンのリアクションタイプ
   * @param {string} clickedReactionType - 押下されたボタンのリアクションタイプ
   * @returns {string} 表示させるボタンのリアクションタイプ
   * @private
   * @remarks
   * 【リアクションタイプの値について】
   *   1. 'none' → いいね（リアクション）されていない状態。押下でいいね（リアクション）される。
   *   2. 'like' → いいねされている状態。
   *   3. カスタムリアクションのurl_name → 該当リアクションが行われている状態。
   */
  determineReactionType(hideReactionType, clickedReactionType) {
    if (hideReactionType === 'none' && clickedReactionType === 'none') {
      return 'like';
    } else if (hideReactionType === clickedReactionType) {
      return 'none';
    } else {
      return clickedReactionType;
    }
  }

  /**
   * UI要素の更新を行う
   * @param {HTMLElement} hideButton - 非表示にするボタン
   * @param {HTMLElement} clickedButton - 押下されたボタン
   * @param {string} displayReactionType - 表示させるリアクションタイプ
   * @param {HTMLElement} reactionPopup - リアクションポップアップ要素
   * @param {string} method - HTTPメソッド（'post'または'delete'）
   * @param {HTMLElement} eyecatch - アニメーション用要素
   */
  updateUI(hideButton, clickedButton, displayReactionType, reactionPopup, method, eyecatch) {
    if (reactionPopup) {
      reactionPopup.classList.add('popup_hide');
    }

    const displayButton = this._findDisplayButton(hideButton, displayReactionType);
    if (displayButton) {
      this._toggleElementVisibility(hideButton, displayButton, method === 'post', eyecatch);
    }
  }

  /**
   * 指定したリアクションタイプを持つ兄弟要素を検索
   * @param {HTMLElement} baseElem - 基準となる要素
   * @param {string} reactionType - 検索するリアっクションタイプ
   * @returns {HTMLElement} 見つかった兄弟要素
   * @private
   */
  _findDisplayButton(baseElem, reactionType) {
    return Array.from(baseElem.parentNode.children)
      .find(child => 
        child !== baseElem &&
        child.dataset.reactionType === reactionType
      );
  }

  /**
   * ボタン要素の表示/非表示を切り替える
   * @param {HTMLElement} hideButton - 非表示にするボタン要素
   * @param {HTMLElement} displayButton - 表示させるボタン要素
   * @param {boolean} isPostAction - いいね（リアクション）操作かどうか
   * @param {HTMLElement} eyecatch - アニメーション要素
   * @private
   */
  _toggleElementVisibility(hideButton, displayButton, isPostAction, eyecatch) {
    hideButton.classList.add('hidden');
    displayButton.classList.remove('hidden');

    if (this.sns_animation) {
      if (isPostAction) {
        hideButton.classList.remove('-unfilled');
        displayButton.classList.add('-filled');

        if (eyecatch) {
          eyecatch.classList.remove('like-anime-hidden');
          eyecatch.classList.add('like-animation');
        }
      } else {
        hideButton.classList.remove('-filled');
        displayButton.classList.add('-unfilled');
      }
    }
  }

  async SendReactionRequest(dataset, method) {
    let reactionParam = '';
    if (!['none', 'like'].includes(dataset.reactionType)) {
      reactionParam = `&like_reaction_attributes[community_reaction_id]=${dataset.reactionType}`;
    }
    return await fetch(
      `${dataset.url}?likeable_type=${dataset.key}&likeable_id=${dataset.value}${reactionParam}`,
      {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document
            .querySelector('meta[name=csrf-token]')
            .getAttribute('content'),
        },
      }
    );
  }

  isLessMD() {
    return window.matchMedia && window.matchMedia('(max-device-width: 640px)').matches;
  }

  touchEvent(elem) {
    if (!this.isLessMD()){ return }

    const reactionPopup = document.querySelector(
      `${this.classname}-popup[data-value="${elem.dataset.value}"]`
    );
    if (!reactionPopup) return;

    elem.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        this.long_touch_event = setTimeout(() => {
          this.popupShowWithTimer(reactionPopup,5000);
          this.long_touch_event = null;
        }, 500);
      }
    );
    elem.addEventListener(
      'touchend',
      () => {
        if (this.long_touch_event){
          clearTimeout(this.long_touch_event);
          elem.click();
        }
        const reactionButtons = document.querySelectorAll(
          `.c-Button-reaction[data-value="${elem.dataset.value}"]`
        );
        reactionButtons.forEach((elem) => {
          if (elem.classList.contains('hovered')){
            elem.click();
            elem.classList.remove('hovered');
            return;
          }
        });
        this.popupHide(reactionPopup);
      }
    );
    elem.addEventListener(
      'touchmove',
      (e) => {
        const touch = e.touches[0];
        const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
        const reactionButtons = document.querySelectorAll(
          `.c-Button-reaction[data-value="${elem.dataset.value}"]`
        );
        reactionButtons.forEach((elem) => {
          if (elem === targetElement.closest('.c-Button-reaction')){
            elem.classList.add('hovered');
          } else {
            elem.classList.remove('hovered');
          }
        });
      }
    );
  }

  popupShowWithTimer(popupElem,duration = 1000) {
    popupElem.classList.remove('popup_hide');
    this.popupHide(popupElem,duration);
  }

  popupShow(popupElem) {
    if (this.hide_event){
      clearTimeout(this.hide_event);
    }
    popupElem.classList.remove('popup_hide');
  }

  popupHide(popupElem,duration = 1000) {
    if (this.hide_event){
      clearTimeout(this.hide_event);
    }
    this.hide_event = setTimeout(() => {
      popupElem.classList.add('popup_hide');
    }, duration);
  }
}
